import { PatchManagementSelectedMaintenanceWindow } from '../../../../endpoints/policyPanel/utils/uiSettings/patchManagement/patch-management.types';
import { MaintenanceWindowTypes } from '../../../../../types/common/maintenanceWindowTypes';
import {
  CompanyItem,
  UserGroupItem,
  FirewallAdvancedProtectionLevel,
  FirewallExclusionItem,
  LocalSandbox,
  UserGroupsResponse,
  UnifiedEndpointsSvaMachine,
  UnifiedEndpointsSvaMachineToUpdate,
  SecurityTelemetryUrlValidity,
  InheritancePolicyData,
  ApplicationInventoryItemResponse,
  EndpointsByActivePolicyResponse,
  CommunicationServerMachineAndSUPA,
  CommunicationServerMachineAndSUPARequest,
} from '@nirvana/shared/dtos/types/policies/common';
import { GetCompaniesRequest } from '@nirvana/shared/dtos/types/dashboards/gz-console';
import { Observable } from 'rxjs';
import { ColumnKeys } from '../../../../endpoints/policyPanel/uiSettings/exchange/general/gridComponents/userGroups/grid-config';
import { GetSummaryRequest } from '../../../../endpoints/policyPanel/utils/uiSettings/exchange/exchange-protection.types';
import { SubscriptionType } from '../../../../../license/types/SubscriptionType';

// api clients for Patch Management module
export interface MaintenanceWindowsApiClients {
  GetMaintenanceWindowById: (
    id: string
  ) => Promise<{ success: boolean; data: PatchManagementSelectedMaintenanceWindow }>;
  GetSuggestedNewMaintenanceWindowName: (type: MaintenanceWindowTypes, companyId: string) => Promise<string>;
  CreateMaintenanceWindowItem: (maintenanceWindow: any) => Promise<{ success: boolean; data: string[] }>;
}
export interface StorageProtectionApiClients {
  ExportStorageExclusions: (policyId: string) => Promise<{ void }>;
}
export interface ImportFileApiClients {
  ImportCsv: (csvFields: string, file: File) => Promise<{ success: boolean; message?: string; data?: any }>;
  ConvertFirewallRulesCsvToJson: (file: File) => Promise<{ success: boolean; data: any }>;
}
export interface ManualSubmissionApiClients {
  getLocalSandboxes: () => Promise<{ success: boolean; data: LocalSandbox[]; total: number }>;
}
export interface ApplicationInventoryApiClients {
  GetApplicationOrGroupByName: (name: string) => Promise<{ data: ApplicationInventoryItemResponse[] }>;
}
export interface NetworkProtectionApiClients {
  AddExclusionToNetworkGrid: (item: FirewallExclusionItem) => void;
  SetNetworkProtectionStatus: (status: boolean) => void;
  ExportWebAccessRules: (policyId: string) => Promise<{ void }>;
}
export interface FirewallRulesApiClients {
  ExportFirewallRules: (policyId: string) => Promise<{ void }>;
  CheckSilentModeState: (
    field: { name: string },
    newValue: FirewallAdvancedProtectionLevel,
    oldValue?: FirewallAdvancedProtectionLevel
  ) => void;
  UncheckSilentModeState: () => void;
}
export interface UserGroupsApiClients {
  ReadRecords: (params: GetSummaryRequest<ColumnKeys>) => Promise<UserGroupsResponse>;
  RemoveRecords: (records: UserGroupItem[]) => Promise<{ success: boolean }>;
  CreateRecords: (records: UserGroupItem[]) => Promise<{ success: boolean }>;
  UpdateRecords: (records: UserGroupItem[]) => Promise<{ success: boolean }>;
}
export interface CompanyApiClients {
  getAvailableCompanies: (request: GetCompaniesRequest) => Observable<{ data: CompanyItem[]; count: number }>;
  getActiveCompany: () => Promise<{ data: CompanyItem }>;
  getCompanySubscription: () => SubscriptionType;
}
export interface UnifiedEndpointsApiClients {
  getSvaMachines: () => Promise<{ data: UnifiedEndpointsSvaMachine[] }>;
  updateSvaMachines: (records: UnifiedEndpointsSvaMachineToUpdate[]) => Promise<boolean>;
}
export interface DiscoveredDevicesApiClients {
  ReadDiscoveredDevices: (params: any) => Promise<{ total: number; data?: any }>;
}
export interface NsxApiClients {
  isNsxPolicyValid: (nsxPolicyName: string, policyId: null | string) => Promise<{ success: boolean }>;
}
export interface PolicyTemplatesApiClients {
  verifySecurityTelemetryUrl: (addressUrl: string) => Promise<{ success: SecurityTelemetryUrlValidity }>;
  getEndpointsByActivePolicy: (policyId: string) => Promise<EndpointsByActivePolicyResponse>;
  isPolicyValid: (policyName: string, policyId: string | null) => Promise<{ success: boolean }>;
  getInheritablePolicies: (
    excludePolicyId: string | null,
    limit: number,
    name: string,
    page: number
  ) => Promise<{ data: InheritancePolicyData[]; total: number }>;
}
export interface AntimalwareApiClients {
  ExportExclusions: (policyId: string) => Promise<{ void }>;
}

export interface ProtectedEntitiesEPSApiClients {
  getCommunicationServerMachinesAndSUPA: (
    params: CommunicationServerMachineAndSUPARequest
  ) => Promise<{ data: CommunicationServerMachineAndSUPA[] }>;
}

// api clients for policy panel
export type PoliciesApiClientsType =
  | MaintenanceWindowsApiClients
  | StorageProtectionApiClients
  | ImportFileApiClients
  | ManualSubmissionApiClients
  | DiscoveredDevicesApiClients
  | NsxApiClients
  | NetworkProtectionApiClients
  | FirewallRulesApiClients
  | UserGroupsApiClients
  | CompanyApiClients
  | UnifiedEndpointsApiClients
  | PolicyTemplatesApiClients
  | AntimalwareApiClients
  | ProtectedEntitiesEPSApiClients;

export interface PoliciesApiClients {
  MaintenanceWindows?: MaintenanceWindowsApiClients;
  StorageProtection?: StorageProtectionApiClients;
  ImportFile?: ImportFileApiClients;
  ManualSubmission?: ManualSubmissionApiClients;
  ApplicationInventory?: ApplicationInventoryApiClients;
  DiscoveredDevices?: DiscoveredDevicesApiClients;
  Nsx?: NsxApiClients;
  NetworkProtection?: NetworkProtectionApiClients;
  FirewallRules?: FirewallRulesApiClients;
  UserGroups?: UserGroupsApiClients;
  Company?: CompanyApiClients;
  UnifiedEndpoints?: UnifiedEndpointsApiClients;
  PolicyTemplates?: PolicyTemplatesApiClients;
  Antimalware?: AntimalwareApiClients;
  ProtectedEntitiesEPS?: ProtectedEntitiesEPSApiClients;
}

// container for policy panel dependencies
export class PolicyDependenciesContainer {
  private static instance: PolicyDependenciesContainer;
  private dependencies: PoliciesApiClients = {};

  private constructor() {}

  static getInstance(): PolicyDependenciesContainer {
    if (!PolicyDependenciesContainer.instance) {
      PolicyDependenciesContainer.instance = new PolicyDependenciesContainer();
    }
    return PolicyDependenciesContainer.instance;
  }

  public registerDependency<T extends keyof PoliciesApiClients>(depIdentifier: T, dependency: PoliciesApiClients[T]) {
    this.dependencies[depIdentifier] = dependency;
  }

  public resolveDependency<T extends keyof PoliciesApiClients>(depIdentifier: T): PoliciesApiClients[T] {
    return this.dependencies[depIdentifier];
  }
}
